/**
 * Pure helpers for the mega menu editor.
 *
 * Everything risky about this screen — draft cloning, dirty comparison and the
 * admin -> public shape adapter that keeps Preview honest — lives here as plain
 * functions with no Vue, no store and no network, so it can be reasoned about
 * and unit-tested in isolation.
 *
 * Vue 2 reactivity note: callers must add array items with splice/push and never
 * by index assignment, and must add new object keys with this.$set. The
 * factories below always return fully-populated objects so that second case
 * does not arise in normal use.
 */

export const CONFIG_VERSION = 1

/* ------------------------------------------------------------------ *
 * Factories — every key present, so Vue 2 can track them from the start
 * ------------------------------------------------------------------ */

export function blankRow () {
  return { label: '', indent: 0, url: null }
}

export function blankColumn () {
  return { heading: '', divider_right: false, rows: [] }
}

export function blankTile () {
  return { image_id: null, url: null, header: '', text: '' }
}

/**
 * Fit a promo tile list to a preset's slot `count` without losing work.
 *
 * Shrinking to a smaller preset does NOT discard the overflow tiles — it moves
 * them into `stash` (kept in slot order); growing refills from the stash first,
 * then pads with blanks. So switching 8 -> 6 -> 8 brings the two tiles and their
 * uploaded images back instead of clearing them (QA).
 *
 * Pure: takes the current tiles and stash, returns the next pair. The caller
 * splices these into its reactive arrays so Vue 2 keeps tracking them.
 */
export function resizeTilesWithStash (tiles, stash, count) {
  const nextTiles = tiles.slice()
  const nextStash = stash.slice()

  if (nextTiles.length > count) {
    // Prepend, so tiles removed now (lower indexes) sit ahead of any stashed by
    // an earlier shrink (higher indexes) — the restore order stays correct.
    const removed = nextTiles.splice(count)
    nextStash.unshift(...removed)
  } else {
    while (nextTiles.length < count) {
      nextTiles.push(nextStash.length ? nextStash.shift() : blankTile())
    }
  }

  return { tiles: nextTiles, stash: nextStash }
}

export function blankPromo () {
  return { header: '', layout: null, tiles: [] }
}

export function blankConfig () {
  return { version: CONFIG_VERSION, columns: [], promo: blankPromo() }
}

/* ------------------------------------------------------------------ *
 * Normalisation — mirrors MegaMenuItem::normaliseConfig() on the server
 * ------------------------------------------------------------------ */

function normaliseRow (row) {
  return {
    // A blank label is meaningful: it renders as a vertical spacer.
    label: typeof row?.label === 'string' ? row.label : '',
    indent: Number.isFinite(Number(row?.indent)) ? Number(row.indent) : 0,
    url: row?.url ?? null
  }
}

function normaliseColumn (column) {
  return {
    // A blank heading is meaningful too: the heading space stays reserved.
    heading: typeof column?.heading === 'string' ? column.heading : '',
    divider_right: !!column?.divider_right,
    rows: Array.isArray(column?.rows) ? column.rows.map(normaliseRow) : []
  }
}

function normaliseTile (tile) {
  return {
    image_id: tile?.image_id != null ? Number(tile.image_id) : null,
    url: tile?.url ?? null,
    header: typeof tile?.header === 'string' ? tile.header : '',
    text: typeof tile?.text === 'string' ? tile.text : ''
  }
}

export function normaliseConfig (config) {
  const source = config && typeof config === 'object' ? config : {}
  const promo = source.promo && typeof source.promo === 'object' ? source.promo : {}

  return {
    version: Number(source.version) || CONFIG_VERSION,
    columns: Array.isArray(source.columns) ? source.columns.map(normaliseColumn) : [],
    promo: {
      header: typeof promo.header === 'string' ? promo.header : '',
      layout: promo.layout ?? null,
      tiles: Array.isArray(promo.tiles) ? promo.tiles.map(normaliseTile) : []
    }
  }
}

/* ------------------------------------------------------------------ *
 * Draft
 * ------------------------------------------------------------------ */

/**
 * A deep, independent editing copy of a server item. Mutating the draft can
 * never reach the store's pristine copy — which is what makes dirty comparison
 * meaningful.
 */
export function toDraft (item) {
  if (!item) return null

  return {
    id: item.id,
    label: item.label ?? '',
    url: item.url ?? null,
    is_visible: item.is_visible !== false,
    has_dropdown: item.has_dropdown !== false,
    open_new_tab: !!item.open_new_tab,
    promo_enabled: !!item.promo_enabled,
    config_object: normaliseConfig(item.config_object)
  }
}

/**
 * The payload sent to PUT /admin/mega-menu/{id}/update.
 */
export function toPayload (draft) {
  return {
    label: draft.label,
    url: emptyToNull(draft.url),
    is_visible: draft.is_visible,
    has_dropdown: draft.has_dropdown,
    open_new_tab: draft.open_new_tab,
    promo_enabled: draft.promo_enabled,
    config_object: {
      version: CONFIG_VERSION,
      columns: draft.config_object.columns.map(column => ({
        heading: column.heading ?? '',
        divider_right: !!column.divider_right,
        rows: column.rows.map(row => ({
          label: row.label ?? '',
          indent: Number(row.indent) || 0,
          url: emptyToNull(row.url)
        }))
      })),
      promo: {
        header: draft.config_object.promo.header ?? '',
        layout: draft.config_object.promo.layout ?? null,
        tiles: draft.config_object.promo.tiles.map(tile => ({
          image_id: tile.image_id ?? null,
          url: emptyToNull(tile.url),
          header: tile.header ?? '',
          text: tile.text ?? ''
        }))
      }
    }
  }
}

function emptyToNull (value) {
  if (value == null) return null
  const trimmed = String(value).trim()
  return trimmed === '' ? null : trimmed
}

/* ------------------------------------------------------------------ *
 * Dirty tracking
 * ------------------------------------------------------------------ */

/**
 * Canonical string form. Keys are emitted in a fixed order rather than relying
 * on JSON.stringify's insertion order, so two structurally identical drafts
 * always compare equal no matter how they were built.
 */
export function canonical (draft) {
  if (!draft) return ''

  const payload = toPayload(draft)

  return JSON.stringify([
    payload.label,
    payload.url,
    payload.is_visible,
    payload.has_dropdown,
    payload.open_new_tab,
    payload.promo_enabled,
    payload.config_object.version,
    payload.config_object.columns.map(column => [
      column.heading,
      column.divider_right,
      column.rows.map(row => [row.label, row.indent, row.url])
    ]),
    [
      payload.config_object.promo.header,
      payload.config_object.promo.layout,
      payload.config_object.promo.tiles.map(tile => [tile.image_id, tile.url, tile.header, tile.text])
    ]
  ])
}

export function isDirty (draft, pristine) {
  return canonical(draft) !== canonical(pristine)
}

/* ------------------------------------------------------------------ *
 * Preview adapter
 * ------------------------------------------------------------------ */

/**
 * Reshape an unsaved draft into exactly the payload GET /api/mega-menu returns,
 * so Preview and the live header can share one set of render components and
 * cannot drift.
 *
 * The only structural difference is the tile image: the draft carries an
 * `image_id`, the public payload carries a resolved `image` object. Ids that no
 * longer resolve become null, matching the server, so a deleted image degrades
 * to text rather than breaking the pane.
 */
export function toPublicShape (draft, imagesById = {}) {
  if (!draft) return null

  const promo = draft.config_object.promo

  return {
    id: draft.id,
    label: draft.label,
    url: draft.url,
    has_dropdown: draft.has_dropdown,
    open_new_tab: draft.open_new_tab,
    columns: draft.config_object.columns.map(column => ({
      heading: column.heading,
      divider_right: !!column.divider_right,
      rows: column.rows.map(row => ({
        label: row.label,
        indent: Number(row.indent) || 0,
        url: row.url
      }))
    })),
    promo: draft.promo_enabled
      ? {
          header: promo.header,
          layout: promo.layout,
          tiles: promo.tiles.map(tile => {
            const image = tile.image_id != null ? imagesById[tile.image_id] : null

            return {
              url: tile.url,
              header: tile.header,
              text: tile.text,
              image: image
                ? { url: image.url, variants: image.variants || {}, crop: image.crop || null }
                : null
            }
          })
        }
      : null
  }
}

/* ------------------------------------------------------------------ *
 * Layout rules — mirrors config/mega-menu.php
 * ------------------------------------------------------------------ */

export const MAX_COLUMNS = 4

/**
 * Derived from LAYOUT_MATRIX rather than hard-coded, so the admin can never
 * block a combination the server accepts. It previously read
 * `columnCount !== 1`, which survived the matrix gaining its 1-column entry and
 * would have kept the UI rejecting what the API had started allowing.
 *
 * Mirrors UpdateMegaMenuItemRequest::validateLayoutCombination.
 */
export function promoSupportedFor (columnCount) {
  return Boolean(LAYOUT_MATRIX[columnCount] && LAYOUT_MATRIX[columnCount].true)
}

export function layoutCombinationError (draft) {
  if (!draft || !draft.promo_enabled) return null

  const count = draft.config_object.columns.length

  return promoSupportedFor(count)
    ? null
    : `No promo pane width is configured for ${count} column(s). ` +
      'Add or remove a column, or turn the promo pane off.'
}

/**
 * Width matrix — MUST mirror config/mega-menu.php `layout_matrix`.
 *
 * Duplicated on the client because Preview resolves widths for an UNSAVED
 * column count, which the server has not seen. `npm`-free cross-check:
 *
 *   docker compose exec app php artisan tinker \
 *     --execute="echo json_encode(config('mega-menu.layout_matrix'));"
 *
 * 1 column + promo pane is absent from both, deliberately — the design never
 * draws it and the admin blocks the combination.
 */
export const LAYOUT_MATRIX = {
  0: { true: { column: null, promo: 1360, gutter: 40 } },
  1: { false: { column: 400, promo: null, gutter: 520 }, true: { column: 560, promo: 800, gutter: 40 } },
  2: { false: { column: 375, promo: null, gutter: 345 }, true: { column: 350, promo: 660, gutter: 40 } },
  3: { false: { column: 350, promo: null, gutter: 195 }, true: { column: 300, promo: 460, gutter: 40 } },
  4: { false: { column: 300, promo: null, gutter: 120 }, true: { column: 255, promo: 340, gutter: 40 } }
}

/** Flexible fallback — a header must render even for a combination we lack. */
export const LAYOUT_FALLBACK = { column: null, promo: null, gutter: 40 }

/**
 * Is a divider meaningful on this column?
 *
 * A divider separates a column from whatever is to its right, so the LAST
 * column never carries one — there is nothing beyond it. The one exception the
 * client gives (walkthrough 00:17-01:54, 03:42-04:37) is a promo pane: then the
 * last column's divider is the line between the columns and the pane.
 *
 * Shared by the admin (which hides the toggle) and the public renderer (which
 * suppresses the line), so the two cannot disagree. The stored value is left
 * alone either way — suppressed, not erased, so restoring the column that used
 * to sit to the right brings the divider back.
 */
export function dividerAllowedFor (index, columnCount, hasPromo) {
  return index < columnCount - 1 || Boolean(hasPromo)
}

/**
 * Which column indexes the centred FILTER SEARCH button sits over.
 *
 * The button is far narrower than any column in the width matrix, so for an odd
 * column count it covers the middle column and for an even count the two either
 * side of the centre line. Those columns have to keep their bottom clear of it;
 * the rest can use the full panel height. This reproduces the list the client
 * walks through at 10:45-11:22 — both columns of two, the second of three, the
 * second and third of four.
 */
export function filterOverlapColumns (columnCount) {
  if (!columnCount) return []

  return columnCount % 2
    ? [(columnCount - 1) / 2]
    : [columnCount / 2 - 1, columnCount / 2]
}

export function resolveLayout (columnCount, hasPromo) {
  const row = LAYOUT_MATRIX[columnCount]

  if (!row) return LAYOUT_FALLBACK

  return row[hasPromo ? 'true' : 'false'] || LAYOUT_FALLBACK
}
